import { baseURL, subdomainAdmin, subdomainContentCreator } from "./config";

/**
 * Sign-in session for the admin / creator panel.
 *
 * The access and refresh tokens live in HttpOnly cookies set by the API, so no
 * script on the page (ours or an injected one) can read them. The browser only
 * keeps a NON-SECRET summary of who is signed in — role, adminId, name, email,
 * image, flag, permissions, status, exp — as returned by login, GET
 * /auth/session and POST /auth/refresh-token. It drives the UI (route guards,
 * sidebar, subdomain routing); the API still checks every request itself.
 *
 * Every request to the API must carry credentials (cookies) and the header
 * `x-nabtt-client`; without the header the API ignores the cookie.
 *
 * The admin panel and the content-creator panel are separate sessions with
 * their own cookies (`web-admin` / `web-creator`) and their own stored summary,
 * so signing in to one never replaces or signs out the other — even in one
 * browser, or on one origin (localhost / main domain, where the creator panel
 * lives under /content-creator).
 */

export const CLIENT_HEADER = "x-nabtt-client";

const isCreatorPanel = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname || "";
  const path = window.location.pathname || "";
  return (
    (subdomainContentCreator && host.includes(subdomainContentCreator)) ||
    path === "/content-creator" ||
    path.startsWith("/content-creator/")
  );
};

// Which panel this page is — decides the cookie set the API uses.
export const clientKind = () => (isCreatorPanel() ? "web-creator" : "web-admin");
const sessionKey = () => (isCreatorPanel() ? "creatorSession" : "adminSession");
// Left behind by the token-in-localStorage version of the panel.
const LEGACY_KEYS = ["token", "refreshToken"];

// 401 codes from the API's authenticate middleware that mean "the session
// cookie is missing, expired or no longer accepted" (not "you may not do this").
const SESSION_ERROR_CODES = new Set([
  "NO_TOKEN",
  "TOKEN_EXPIRED",
  "TOKEN_REVOKED",
  "INVALID_TOKEN",
  "INVALID_TOKEN_FORMAT",
  "ACCOUNT_NOT_FOUND",
  "ACCOUNT_INACTIVE",
]);

export function apiRoot() {
  const url = baseURL || "";
  return url.endsWith("/") ? url : `${url}/`;
}

/** True when `url` goes to our API (relative URLs resolve against it). */
export function isApiUrl(url) {
  if (!url || typeof url !== "string") return true;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(url) && !url.startsWith("//")) return true;
  try {
    const target = new URL(url, window.location.href);
    const api = new URL(apiRoot() || "/", window.location.href);
    return target.origin === api.origin;
  } catch (_) {
    return false;
  }
}

/** Headers every API request needs (fetch / XHR); merge with your own. */
export function apiHeaders(extra = {}) {
  return { ...extra, [CLIENT_HEADER]: clientKind() };
}

/** Call after xhr.open() for an XMLHttpRequest to the API. */
export function prepareApiXhr(xhr) {
  xhr.withCredentials = true;
  xhr.setRequestHeader(CLIENT_HEADER, clientKind());
}

// ---------------------------------------------------------------------------
// Stored session summary
// ---------------------------------------------------------------------------

const listeners = new Set();

/** Be told when the stored session changes (null = signed out). */
export function subscribeSession(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(session) {
  listeners.forEach((listener) => {
    try {
      listener(session);
    } catch (_) {
      /* a listener must never break sign-in */
    }
  });
}

const validSession = (value) =>
  value && typeof value === "object" && typeof value.role === "string" && value.role ? value : null;

/** The signed-in account's non-secret claims, or null. */
export function getSession() {
  try {
    return validSession(JSON.parse(localStorage.getItem(sessionKey()) || "null"));
  } catch (_) {
    return null;
  }
}

export function getRole() {
  return getSession()?.role || "";
}

/** The panel believes someone is signed in (the API has the final word). */
export function isSignedIn() {
  return Boolean(getSession());
}

export function saveSession(session) {
  const valid = validSession(session);
  if (!valid) return null;
  signingOut = false;
  try {
    localStorage.setItem(sessionKey(), JSON.stringify(valid));
  } catch (_) {
    /* storage full / blocked: the redux state still holds it for this tab */
  }
  notify(valid);
  return valid;
}

export function clearSession() {
  try {
    localStorage.removeItem(sessionKey());
  } catch (_) {
    /* ignore */
  }
  notify(null);
}

/** Drop tokens an older build stored in localStorage. */
export function purgeLegacyTokens() {
  try {
    LEGACY_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch (_) {
    /* ignore */
  }
}

/** Storage key, for cross-tab `storage` events. */
// The storage key of THIS panel's session summary (for the cross-tab listener).
export const sessionStorageKey = () => sessionKey();

// ---------------------------------------------------------------------------
// Login redirect
// ---------------------------------------------------------------------------

function loginPath(role) {
  const host = window.location.hostname;
  if (host.includes(subdomainContentCreator) || host.includes(subdomainAdmin)) {
    return "/login";
  }
  if (role === "CONTENT_CREATOR" || window.location.pathname.startsWith("/content-creator")) {
    return "/content-creator/login";
  }
  return "/login";
}

/** Drop the session and send the browser to the matching login page. */
export function forceLogin() {
  if (window.__nabttAuthRedirecting) return;
  const path = loginPath(getRole());
  clearSession();
  if (window.location.pathname === path) return;
  window.__nabttAuthRedirecting = true;
  window.location.href = path;
}

// ---------------------------------------------------------------------------
// Refresh
// ---------------------------------------------------------------------------

let refreshInFlight = null;
let lastRefreshAt = 0;
let signingOut = false;

/** A 401 that means the session cookie is gone, expired or rejected. */
export function isSessionError(status, code) {
  return status === 401 && SESSION_ERROR_CODES.has(code);
}

/** Refreshing can help: we think we are signed in and are not signing out. */
export function canRefresh() {
  return !signingOut && isSignedIn();
}

const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Ask the API for new session cookies (the refresh cookie is sent
 * automatically). One call is shared by every request that failed at the same
 * time, so the refresh token is rotated once. Resolves with the session;
 * rejects — after sending the browser to the login page — when the session is
 * over. A network failure rejects without signing out.
 */
export function refreshSession() {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = fetch(`${apiRoot()}auth/refresh-token`, {
    method: "POST",
    credentials: "include",
    headers: apiHeaders(),
  })
    .then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.status && data.alreadyRefreshed) {
        // Another tab rotated the cookies at this moment. Give its response
        // time to land in the cookie jar, then confirm the session with the
        // API before the caller retries — never sign every tab out just because
        // the winning tab's cookies arrived a little later.
        for (const wait of [400, 1200]) {
          await pause(wait);
          const check = await fetch(`${apiRoot()}auth/session`, {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: apiHeaders(),
          }).catch(() => null);
          const body = check ? await check.json().catch(() => ({})) : {};
          if (check?.ok && body?.status && body.session) {
            lastRefreshAt = Date.now();
            return saveSession(body.session);
          }
        }
        lastRefreshAt = Date.now();
        return getSession();
      }
      if (!response.ok || !data?.status || !data.session) {
        const error = new Error(data?.message || "Your session has ended. Please sign in again.");
        error.code = data?.code || "";
        error.status = response.status;
        error.sessionEnded = true;
        throw error;
      }
      lastRefreshAt = Date.now();
      return saveSession(data.session);
    })
    .catch((error) => {
      if (error?.sessionEnded) forceLogin();
      throw error;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

/**
 * For a request that failed with a session error: refresh, unless a refresh
 * already finished after that request was sent (then just retry it).
 */
export function ensureFreshSession(sentAt) {
  if (refreshInFlight) return refreshInFlight;
  if (sentAt && sentAt < lastRefreshAt) return Promise.resolve(getSession());
  return refreshSession();
}

/** Refresh ahead of time when the access token is about to expire (sockets, big uploads). */
export function refreshIfExpiringSoon(withinMs = 60000) {
  const exp = Number(getSession()?.exp) || 0;
  if (!exp || exp * 1000 - Date.now() > withinMs) return Promise.resolve(getSession());
  return refreshSession();
}

/**
 * Send an XMLHttpRequest to the API (after xhr.open and prepareApiXhr). An
 * upload cannot be replayed cheaply, so the session is refreshed first when
 * the access token is about to expire.
 */
export function sendApiXhr(xhr, body) {
  refreshIfExpiringSoon()
    .catch(() => {})
    .then(() => xhr.send(body));
}

// ---------------------------------------------------------------------------
// Confirm on load / sign out
// ---------------------------------------------------------------------------

/**
 * Ask the API who is signed in (GET /auth/session). Refreshes once when the
 * access cookie has expired. Resolves with the session, or null when signed
 * out (the stored summary is then cleared). Network failures keep the stored
 * summary and resolve with it.
 */
export async function confirmSession() {
  // A sign-out that never reached the API: finish it first.
  if (await finishPendingSignOut()) return null;
  const load = () =>
    fetch(`${apiRoot()}auth/session`, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      headers: apiHeaders(),
    });
  try {
    const sentAt = Date.now();
    let response = await load();
    let data = await response.json().catch(() => ({}));
    if (isSessionError(response.status, data?.code) && canRefresh()) {
      await ensureFreshSession(sentAt);
      response = await load();
      data = await response.json().catch(() => ({}));
    }
    if (response.ok && data?.status && data.session) return saveSession(data.session);
    if (response.status === 401) {
      clearSession();
      return null;
    }
    return getSession();
  } catch (error) {
    if (error?.sessionEnded) return null;
    return getSession();
  }
}

let pendingSignOut = Promise.resolve();
const signOutMarkKey = () => `signOutPending:${clientKind()}`;

const sendRevoke = () =>
  fetch(`${apiRoot()}auth/revoke-token`, {
    method: "POST",
    credentials: "include",
    headers: apiHeaders(),
  }).then((response) => {
    if (!response.ok) throw new Error(`revoke failed: ${response.status}`);
    try {
      localStorage.removeItem(signOutMarkKey());
    } catch (_) {}
  });

/**
 * Sign out: forget the session here at once (so in-flight requests that now
 * fail do not try to refresh it), then have the API revoke the tokens and
 * clear the cookies. If that request does not get through (offline, page
 * closed), a mark is left so the next page load finishes the sign-out before
 * it checks the session — otherwise the still-valid cookies would sign the
 * user straight back in.
 */
export function revokeSession() {
  signingOut = true;
  clearSession();
  try {
    localStorage.setItem(signOutMarkKey(), String(Date.now()));
  } catch (_) {}
  pendingSignOut = sendRevoke()
    .catch(() => {})
    .then(() => undefined);
  return pendingSignOut;
}

// Finishes a sign-out that did not reach the API earlier. Resolves true when
// one was pending (the caller should then treat the user as signed out).
async function finishPendingSignOut() {
  let pending = null;
  try {
    pending = localStorage.getItem(signOutMarkKey());
  } catch (_) {}
  if (!pending) return false;
  await sendRevoke().catch(() => {});
  clearSession();
  return true;
}

/** Resolves once a sign-out still on the wire has finished (login waits for it). */
export function afterSignOut() {
  return pendingSignOut;
}
